import React from 'react';
import { Avatar, Divider, Flex, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import CustomTitle from '@/components/CustomTitle';
import CustomParagraph from '@/components/CustomParagraph';
import CustomInput from '@/components/CustomInput';
import { processSensorList } from '@/common/utils/sensorUtils';
import type { Sensor, SensorFormProps } from './types';
import './sensors.css';

/**
 * Form component for selecting and adding sensors
 *
 * Displays a searchable list of available sensor providers with filtering capabilities.
 * When a sensor is selected, it transforms the data and opens a modal for configuration.
 *
 * @param getSensorImagePath - Function to retrieve sensor image paths
 * @param loading - Loading state for sensor list
 * @param searchQuery - Current search query string
 * @param sensorList - Array of available sensor providers
 * @param setSearchQuery - Callback to update search query
 * @param setSelectedSensor - Callback when a sensor is selected
 * @param setModalOpen - Callback to open/close the configuration modal
 */
const SensorForm: React.FC<SensorFormProps> = ({
  getSensorImagePath,
  loading,
  onSensorSelect,
  searchQuery,
  sensorList,
  setSearchQuery,
  setSelectedSensor,
  setModalOpen,
  setShowProviderDetails,
}) => {
  const processedSensors = processSensorList(sensorList);
  const filteredSensors = processedSensors?.filter((provider) =>
    provider?.name?.toLowerCase().includes(searchQuery?.toLowerCase())
  );

  const handleSelect = (provider: Sensor) => {
    const updatedProvider: Sensor = {
      ...provider,
      ...(provider?.description && {
        credential_description: provider.description,
      }),
    };

    // Remove original 'description' if it exists
    if (provider?.description) delete updatedProvider.description;

    if (onSensorSelect) {
      onSensorSelect(updatedProvider);
      return;
    }

    setSelectedSensor(updatedProvider);
    if (setShowProviderDetails) {
      setShowProviderDetails(true);
      return;
    }
    setModalOpen?.(true);
  };

  return (
    <div className='sensor-full-height-column'>
      <div className='drawer-title-header'>
        <CustomTitle
          text='Add sensor'
          fontSize={20}
          fontWeight='700'
          lineHeight='32px'
          color='var(--text-primary)'
          marginBottom={'10px'}
        />
        <CustomInput
          width={'98%'}
          bgColor='var(--light-gray)'
          height='36px'
          textColor='var(--text-primary)'
          className='custom-input form-input-style'
          borderRadius='20px'
          border='none'
          placeholderColor='var(--text-secondary)'
          placeholder='Search for a sensor...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          prefix={<SearchOutlined className='sensor-search-icon' />}
        />
      </div>

      <div className='custom-scrollbar sensor-form-list'>
        {loading ? (
          <Flex
            justify='center'
            align='center'
            className='sensor-centered-fill'
          >
            <Spin />
          </Flex>
        ) : filteredSensors?.length > 0 ? (
          <Flex wrap='wrap' className='sensor-provider-grid'>
            {filteredSensors.map((provider, index) => {
              const providerName = provider?.name ?? '';
              const sensorImagePath = getSensorImagePath(
                providerName,
                'SensorImages'
              );
              return (
                <React.Fragment key={provider.id}>
                  <Flex
                    align='center'
                    justify='space-between'
                    className='custom-select sensor-provider-option'
                    onClick={() => handleSelect(provider)}
                  >
                    <Flex
                      align='center'
                      gap={10}
                      className='sensor-provider-option-content'
                    >
                      <Avatar
                        size={34}
                        src={
                          <img
                            src={sensorImagePath}
                            alt='avatar'
                            className='sensor-provider-image'
                          />
                        }
                        className={`sensor-provider-avatar ${
                          sensorImagePath
                            ? 'sensor-provider-avatar-transparent'
                            : 'sensor-provider-avatar-fallback'
                        }`}
                      >
                        {!sensorImagePath && providerName.charAt(0)}
                      </Avatar>
                      <CustomParagraph
                        marginBottom={'0px'}
                        text={provider.name}
                        fontSize='12px'
                        fontWeight={600}
                      />
                    </Flex>
                  </Flex>

                  {(index + 1) % 2 === 0 && (
                    <Divider className='sensor-provider-divider' />
                  )}
                </React.Fragment>
              );
            })}
          </Flex>
        ) : (
          <Flex
            justify='center'
            align='center'
            className='sensor-centered-fill'
          >
            <CustomParagraph text='No sensors available' fontSize='14px' />
          </Flex>
        )}
      </div>
    </div>
  );
};

export default SensorForm;
